import { cn } from '@newfront-insurance/core-ui/v2';
import type { FormEvent, ReactNode } from 'react';

import { useAttachments } from './attachments-provider';
import { useChatInputContext } from './chat-input-context';
import { useDragHandlersRef } from './chat-input-context';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { novaGradientStateClass } from '../../design-system';

interface ChatInputProps {
  children: ReactNode;
  className?: string;
  fullWidth?: boolean;
}

interface ChatInputFrameProps {
  children: ReactNode;
  className?: string;
  isDragging?: boolean;
  isPublicMode?: boolean;
  onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
}

/** Shared visual and drag/drop surface for Nova prompt editors. */
export function ChatInputFrame({
  children,
  className,
  isDragging = false,
  isPublicMode = false,
  onDragOver,
  onDragLeave,
  onDrop,
}: ChatInputFrameProps) {
  return (
    <div
      className={cn(
        'group relative w-full rounded-[12px] p-[2px]',
        'bg-steel-200',
        novaGradientStateClass.aiBorderIconHover,
        novaGradientStateClass.aiBorderIconFocusWithin,
        {
          'bg-transparent': isDragging,
          'bg-pink-yellow-gradient focus-within:bg-pink-yellow-gradient': isPublicMode,
        },
        className,
      )}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="flex w-full flex-col gap-0 overflow-hidden rounded-[10px] bg-white shadow-[0_0_0_1px_white] group-focus-within:shadow-none">
        {children}
      </div>
    </div>
  );
}

/**
 * Root wrapper component for chat input.
 * Provides the form container and drag/drop handlers.
 * Handlers come from MessagePayloadProvider (populated by ChatAttachments if rendered).
 *
 * @example
 * ```tsx
 * <ChatInput>
 *   <ChatInputTextarea />
 *   <ChatInputToolbar>
 *     <ChatAttachments />
 *     <SendButton />
 *   </ChatInputToolbar>
 * </ChatInput>
 * ```
 */
export function ChatInput({ children, className, fullWidth = false }: ChatInputProps) {
  const context = useChatInputContext();
  const { handleSubmit, isPublicMode } = context;
  const { attachments } = useAttachments();
  const { additionalContext } = useChatOverlayContext();
  const hasUploadingFiles = attachments.some((attachment) => attachment.isUploading);
  const successfulAttachmentUuids = attachments
    .filter((attachment) => attachment.fileUuid && !attachment.uploadError)
    .map((attachment) => attachment.fileUuid!);
  const contextAttachmentFileUuids = Array.isArray(additionalContext?.attachmentFileUuids)
    ? additionalContext.attachmentFileUuids.filter((uuid): uuid is string => typeof uuid === 'string')
    : [];
  const hasUnsyncedAttachmentContext =
    successfulAttachmentUuids.length !== contextAttachmentFileUuids.length ||
    successfulAttachmentUuids.some((uuid) => !contextAttachmentFileUuids.includes(uuid));
  // Read drag handlers from ref (populated by ChatAttachments if rendered)
  const handlersRef = useDragHandlersRef();
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (hasUploadingFiles || hasUnsyncedAttachmentContext) {
      return;
    }
    handleSubmit(event);
  };

  return (
    <div className={cn('flex-shrink-0 px-2', className)}>
      <form className={cn('mx-auto flex w-full gap-2', !fullWidth && 'max-w-3xl')} onSubmit={handleFormSubmit}>
        <ChatInputFrame
          isPublicMode={isPublicMode}
          onDragOver={(event) => handlersRef.current.handleDragOver?.(event)}
          onDragLeave={(event) => handlersRef.current.handleDragLeave?.(event)}
          onDrop={(event) => handlersRef.current.handleDrop?.(event)}
        >
          {children}
        </ChatInputFrame>
      </form>
    </div>
  );
}
